import React, {useCallback, useEffect, useState} from 'react';
import classNames from 'classnames';
import 'stylesheets/admin.scss';
import ReportIntegrationService, {
  ReportIntegration,
} from '../../services/report-integration-service';
import ScheduledReportService, {ScheduledReport} from '../../services/scheduled-report-service';
import Button from 'src/components/button';
import {ReportIntegrationModal} from './report-integration-modal';
import Icon from 'shared/components/icon';
import {IconButton} from '@mui/material';
import {useDispatch} from 'react-redux';
import {errorMessage, flashMessage} from 'shared/actions/notifications';
import Loading from 'shared/components/loading';
import Checkbox from 'shared/components/checkbox';
import styles from './styles.module.scss';
import Card from '../Cards/Card';
import req from '../../../shared/req';
import AreYouSure from 'src/components/AreYouSure';

interface Props {
  universityId: string;
  enableReportIntegrations: boolean;
}

export default function ReportIntegrations({universityId, enableReportIntegrations}: Props) {
  const [reportIntegrations, setReportIntegrations] = useState<ReportIntegration[]>([]);
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [integrationsEnabled, setIntegrationsEnabled] = useState(enableReportIntegrations);
  const [editIntegrationId, setEditIntegrationId] = useState<string | null>(null);
  const dispatch = useDispatch();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [integrations, reports] = await Promise.all([
        ReportIntegrationService.index({universityId}),
        ScheduledReportService.index(),
      ]);
      setReportIntegrations(integrations);
      setScheduledReports(reports.filter(({active}) => active !== false));
    } catch (err) {
      dispatch(errorMessage('Failed to load integrations'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [universityId, dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleEnabled = async () => {
    try {
      await req({
        url: `/v1/admin/university/${universityId}/update-report-integrations-setting`,
        method: 'PATCH',
        data: {enable: !integrationsEnabled},
      });

      setIntegrationsEnabled(!integrationsEnabled);

      dispatch(flashMessage('Event Notification Updated'));
    } catch (error) {
      console.log({error});
    }
  };

  const updateIntegration = (integration: ReportIntegration) => {
    setReportIntegrations((prev) => {
      const existing = prev.find((i) => i.id === integration.id);
      if (existing) {
        return prev.map((i) => (i.id === integration.id ? integration : i));
      }
      return [...prev, integration];
    });
  };

  const deleteIntegration = async (id: string) => {
    try {
      await ReportIntegrationService.destroy(id, {universityId});
      setReportIntegrations((prev) => prev.filter((i) => i.id !== id));
      dispatch(flashMessage('Integration deleted successfully'));
    } catch (err) {
      dispatch(errorMessage('Failed to delete integration'));
      console.error(err);
    }
  };

  const getIntegrationDetails = (integration: ReportIntegration) => {
    if (integration.type === 's3' && integration.s3Config) {
      return `S3 Bucket: ${integration.s3Config.bucketName}, prefix: ${integration.s3Config.prefix}`;
    } else if (integration.type === 'sftp' && integration.sftpConfig) {
      return `SFTP Server: ${integration.sftpConfig.host}, path: ${integration.sftpConfig.path}`;
    }
    return '';
  };

  const newIntegration: Partial<ReportIntegration> = {
    name: '',
    type: 's3',
    enabledReports: [],
    s3Config: {
      bucketName: '',
      prefix: '/',
      region: 'us-east-1',
      authMethod: 'bucket_policy',
    },
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      {editIntegrationId && (
        <ReportIntegrationModal
          initialValues={
            editIntegrationId === 'new'
              ? newIntegration
              : reportIntegrations.find((i) => i.id === editIntegrationId) || newIntegration
          }
          updateIntegration={updateIntegration}
          universityId={universityId}
          scheduledReports={scheduledReports}
          onClose={() => setEditIntegrationId(null)}
        />
      )}

      <div className={styles.header}>
        <div className={styles.enableIntegrationsSection}>
          <Checkbox
            checked={integrationsEnabled}
            onChange={handleToggleEnabled}
            label={<span className="shared-font body bold">Enable Integrations</span>}
          />
          <p className={classNames('shared-font small', styles.helpText)}>
            When disabled, no reports will be sent to any integration endpoints
          </p>
        </div>
      </div>

      <div className={classNames({[styles.disabledState]: !integrationsEnabled})}>
        <div className={styles.configuredIntegrationsHeader}>
          <h2 className="shared-font title">Configured Integrations</h2>
          <Button onClick={() => setEditIntegrationId('new')} outline size="tiny">
            + Add New Integration
          </Button>
        </div>

        {reportIntegrations.length === 0 ? (
          <div className={styles.emptyState}>
            <p className="shared-font body">No integrations configured yet.</p>
            <p className={classNames('shared-font small', styles.emptyStateSubtext)}>
              Click &#34;Add New Integration&#34; to get started.
            </p>
          </div>
        ) : (
          <div>
            {reportIntegrations.map((integration) => (
              <Card key={integration.id} className={styles.integrationCard}>
                <div className={styles.integrationInfo}>
                  <p className={classNames('shared-font body bold', styles.integrationName)}>
                    {integration.name}
                  </p>
                  <p className={classNames('shared-font small', styles.integrationDetails)}>
                    {getIntegrationDetails(integration)}
                  </p>
                  <p className={classNames('shared-font small', styles.integrationReportCount)}>
                    Reports Enabled:{' '}
                    {scheduledReports
                      .filter((report) =>
                        (integration.enabledReports || []).some(
                          (enabledReport) =>
                            enabledReport.savedSearchId === report._id &&
                            enabledReport.newFollowersOnly === report.newFollowersOnly
                        )
                      )
                      .map((report) => report.filename)
                      .join(', ') || 'None'}
                  </p>
                </div>
                <div className={styles.integrationActions}>
                  <IconButton
                    size="medium"
                    onClick={() => setEditIntegrationId(integration.id)}
                    className={styles.editButton}
                  >
                    <Icon iconType="pen" />
                  </IconButton>
                  <AreYouSure
                    id={`delete-${integration.id}`}
                    onYes={() => deleteIntegration(integration.id)}
                    buttonName={`delete-integration-${integration.id}`}
                  >
                    <IconButton size="medium" className={styles.deleteButton}>
                      <Icon iconType="trash" />
                    </IconButton>
                  </AreYouSure>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
